import { Boxes, Factory, Tags, Target, Truck, type LucideIcon } from 'lucide-react'
import { z } from 'zod'
import { defaultForecastRange } from '../previsions-produits/-search-schema'
import { PAID_CA_LABEL } from './-previsions-format'

export const MEASURES = ['uvc', 'ca', 'tonnage'] as const
export const SEGMENTS = ['uf', 'commercial', 'industriel', 'reappro', 'logistique'] as const

export type Measure = (typeof MEASURES)[number]
export type Segment = (typeof SEGMENTS)[number]

export const PREVISIONS_VIEWS = ['ventes', 'production', 'logistique'] as const
const PROJECTION_MEASURES = ['tonnage', 'uvc', 'caisses', 'palis'] as const

export type PrevisionsView = (typeof PREVISIONS_VIEWS)[number]
export type ProjectionView = Exclude<PrevisionsView, 'ventes'>
export type ProjectionMeasure = (typeof PROJECTION_MEASURES)[number]

/** Mesures du graphique de chaque vue projection, la première étant celle par défaut. */
export const PROJECTION_VIEW_MEASURES: Record<ProjectionView, readonly ProjectionMeasure[]> = {
  production: ['tonnage', 'uvc'],
  logistique: ['uvc', 'caisses', 'palis'],
}

export const resolveProjectionMeasure = (
  view: ProjectionView,
  measure: ProjectionMeasure | undefined,
): ProjectionMeasure => {
  const measures = PROJECTION_VIEW_MEASURES[view]
  return measure !== undefined && measures.includes(measure) ? measure : measures[0]
}

/** Décomposition proposée à l'ouverture de chaque vue. */
export const DEFAULT_SEGMENTS: Record<PrevisionsView, Segment> = {
  ventes: 'commercial',
  production: 'industriel',
  logistique: 'reappro',
}

export const resolveSegment = (view: PrevisionsView, segment: Segment | undefined): Segment =>
  segment ?? DEFAULT_SEGMENTS[view]

export const previsionsSearchSchema = z.object({
  storeIds: z.array(z.number().int().positive()).optional(),
  startDate: z.iso.date().optional().catch(undefined),
  endDate: z.iso.date().optional().catch(undefined),
  categoryIds: z.array(z.number().int().nonnegative()).optional().default([]),
  kitchenIds: z.array(z.number().int().positive()).optional().default([]),
  measure: z.enum(MEASURES).optional().default('uvc'),
  /** Absent : la décomposition par défaut de la vue (`resolveSegment`). */
  segment: z.enum(SEGMENTS).optional(),
  capping: z.boolean().catch(false),
  includeProjects: z.boolean().optional().default(false),
  projectIds: z.array(z.number().int().positive()).optional(),
  focusDate: z.iso.date().optional().catch(undefined),
  view: z.enum(PREVISIONS_VIEWS).catch('ventes'),
  projectionMeasure: z.enum(PROJECTION_MEASURES).optional().catch(undefined),
  /** Vues Production et Logistique : jour du calcul de réappro rejoué. Absent = dernier calcul. */
  runDate: z.iso.date().optional().catch(undefined),
})

export type PrevisionsSearch = z.infer<typeof previsionsSearchSchema>

export interface PrevisionsRange {
  start: string
  end: string
}

export const resolvePrevisionsRange = (search: PrevisionsSearch): PrevisionsRange => {
  const fallback = defaultForecastRange()
  return {
    start: search.startDate ?? fallback.start,
    end: search.endDate ?? fallback.end,
  }
}

export const selectedStoreIdsOf = (
  search: Pick<PrevisionsSearch, 'storeIds' | 'includeProjects'>,
  allStoreIds: readonly number[],
): number[] => {
  const { storeIds } = search
  // Avant la sélection explicite, `[]` voulait dire « toutes » : les anciens liens le portent encore.
  if (storeIds === undefined || (storeIds.length === 0 && !search.includeProjects)) {
    return [...allStoreIds]
  }
  return storeIds
}

export const selectedProjectIdsOf = (
  search: Pick<PrevisionsSearch, 'projectIds' | 'includeProjects'>,
  allProjectIds: readonly number[],
): number[] => (search.includeProjects ? (search.projectIds ?? [...allProjectIds]) : [])

const coversAll = (selected: readonly number[], all: readonly number[]): boolean =>
  all.length > 0 && all.every((id) => selected.includes(id))

/**
 * Paramètres d'URL d'un périmètre appliqué par le sélecteur de boutiques (ou par ⌘S, qui lui passe
 * une seule boutique et aucun projet) : une liste qui couvre tout est omise, et retirer tous les
 * projets désactive leur ajout.
 */
export const scopeSearch = (
  next: { storeIds: number[]; projectIds: number[] },
  current: {
    includeProjects: boolean
    allStoreIds: readonly number[]
    allProjectIds: readonly number[]
  },
): Partial<PrevisionsSearch> => {
  const dropsAllProjects = current.includeProjects && next.projectIds.length === 0
  return {
    storeIds: coversAll(next.storeIds, current.allStoreIds) ? undefined : next.storeIds,
    projectIds:
      dropsAllProjects || coversAll(next.projectIds, current.allProjectIds)
        ? undefined
        : next.projectIds,
    ...(dropsAllProjects ? { includeProjects: false } : {}),
  }
}

export const isDateInRange = (date: string, range: PrevisionsRange): boolean =>
  date >= range.start && date <= range.end

export const MEASURE_LABELS: Record<Measure | ProjectionMeasure, string> = {
  uvc: 'UVC',
  ca: PAID_CA_LABEL,
  tonnage: 'Tonnage',
  caisses: 'Caisses',
  palis: 'Palis',
}

export const SEGMENT_LABELS: Record<Segment, string> = {
  uf: 'UF / Negoce',
  commercial: 'Commercial',
  industriel: 'Industriel',
  reappro: 'Réappro',
  logistique: 'Logistique',
}

export const SEGMENT_ICONS: Record<Segment, LucideIcon> = {
  uf: Target,
  commercial: Tags,
  industriel: Factory,
  reappro: Truck,
  logistique: Boxes,
}

export type CategorySource = 'commercial' | 'internal' | 'reappro' | 'logistics'

export const SEGMENT_CATEGORY_SOURCE: Record<Segment, CategorySource> = {
  uf: 'commercial',
  commercial: 'commercial',
  industriel: 'internal',
  reappro: 'reappro',
  logistique: 'logistics',
}

/**
 * Catégories par source, chacune vide tant qu'elle n'est pas chargée : commerciales (avec leurs
 * produits) et celles des axes interne, réappro et logistique (`forecast.segment_categories`, qui
 * termine les catégories logistiques par « Non renseigné »).
 */
export const categoriesBySourceOf = <C>(
  commercial: C[] | undefined,
  segmentCategories: { internal: C[]; reappro: C[]; logistics: C[] } | undefined,
): Record<CategorySource, C[]> => ({
  commercial: commercial ?? [],
  internal: segmentCategories?.internal ?? [],
  reappro: segmentCategories?.reappro ?? [],
  logistics: segmentCategories?.logistics ?? [],
})

/** Filtre de catégories des requêtes de la vue Ventes. */
export interface ProductScopeQuery {
  productIds?: number[]
  logisticsCategoryIds?: number[]
}

/**
 * Filtre de catégories des requêtes de la vue Ventes : les produits des catégories choisies, sauf
 * sur l'axe logistique, dont le serveur résout les ids de catégorie (« Non renseigné » compte plus
 * de produits que la limite de 1000 `productIds`). Rien sans catégorie choisie.
 */
export const productScopeQueryOf = (
  source: CategorySource,
  selected: readonly { id: number; products: readonly { id: number }[] }[],
): ProductScopeQuery => {
  if (selected.length === 0) return {}
  if (source === 'logistics')
    return { logisticsCategoryIds: selected.map((category) => category.id) }
  const productIds = selected.flatMap((category) => category.products.map((product) => product.id))
  return productIds.length > 0 ? { productIds } : {}
}

/**
 * La vue lit-elle les catégories commerciales et leurs produits ? Logistique n'a pas de dates de
 * lancement : elles n'y servent qu'au filtre d'une décomposition commerciale ou UF.
 */
export const needsProductCategories = (
  view: PrevisionsView,
  segment: Segment | undefined,
): boolean =>
  view !== 'logistique' || SEGMENT_CATEGORY_SOURCE[resolveSegment(view, segment)] === 'commercial'

/** Changer de décomposition vide le filtre de catégories, qui change d'axe avec elle. */
export const segmentChangeSearch = (
  segment: Segment,
): Pick<PrevisionsSearch, 'segment' | 'categoryIds'> => ({ segment, categoryIds: [] })

/** Axe de regroupement attendu par l'endpoint des séries journalières. */
export const SEGMENT_GROUP_BY: Record<
  Segment,
  'uf' | 'category' | 'internal' | 'reappro' | 'logistics'
> = {
  uf: 'uf',
  commercial: 'category',
  industriel: 'internal',
  reappro: 'reappro',
  logistique: 'logistics',
}

export type AxisGroupBy = (typeof SEGMENT_GROUP_BY)[Segment]

/**
 * Décomposition d'une réponse, relue sur l'axe qu'elle porte : l'inverse de `SEGMENT_GROUP_BY`
 * (`-search-schema.test.mjs` le vérifie), complet par `satisfies`.
 */
export const GROUP_BY_SEGMENT = {
  uf: 'uf',
  category: 'commercial',
  internal: 'industriel',
  reappro: 'reappro',
  logistics: 'logistique',
} as const satisfies Record<AxisGroupBy, Segment>

export const PREVISIONS_EXPORT_GROUP_BYS: AxisGroupBy[] = SEGMENTS.map(
  (segment) => SEGMENT_GROUP_BY[segment],
)

export const effectiveCapping = (canEditCapping: boolean, requested: boolean): boolean =>
  canEditCapping ? requested : false
